import React, { useEffect, useState, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Sidebar from "./components/Sidebar/Sidebar";
import Dashboard from "./pages/Dashboard";
import UserManagement from "./pages/UserManagement";
import SenderManagement from "./pages/SenderManagement";
import ReceiverManagement from "./pages/ReceiverManagement";
import CustomsAgent from "./pages/CustomsAgent";
import DeclarationManagement from "./pages/DeclarationManagement";
import DeclarationView from "./pages/DeclarationView";
import Success from './pages/Success';
import Payments from './pages/Payments';
import Cancel from './pages/Cancel';
import { TaricJsonErrors } from "./components/Main/TaricJsonErrors";

import { useUserLoaded, useSenders, useAgents, useReceivers, useCompany } from './hooks/useApiHooks';

import logoutIcon from "./assets/logout-icon.png";

import english from "./assets/united-states.png";
import spanish from "./assets/spain.png";

import { ArrowUpLeft, OctagonAlert, Languages, Save, X, FileJson, RotateCcw } from 'lucide-react';

import "./App.css";
import { gsap } from 'gsap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCreditStats, setExtractedData, setDeclarationDirty, clearLastEditedDocumentField } from './store/reducers';

import { useDeclarationSummary } from "./hooks/useDeclarationSummary";
import { ValidationErrors } from "./components/Dashboard/DeclarationSummary/ValidationErrors";

import toast from 'react-hot-toast';
import CreditSnippet from './components/CreditSnippet';
import StripePaymentModal from './components/StripePaymentModal';
import Cookies from 'js-cookie';
import { useLanguage, getMainTranslation, getDeclarationManagementTranslation } from './hooks/useLanguage';
import { getDeclarationSummaryTranslation } from './hooks/useLanguage';
import { FieldOverviewPanel } from './components/Dashboard/FieldOverviewPanel';
import { GenerateTab } from './components/Main/GenerateTab';
import { resetAppState } from './utils/resetAppState';

function Main({ isDarkMode, setIsDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const showBacktoDashboardButton = useSelector(state => state.ui.showBacktoDashboardButton);
  const userData = useSelector((state) => state.user.userData);
  const taricJsonErrors = useSelector((state) => state.preparedJson.taricJsonErrors);
  const validationErrors = useSelector((state) => state.validationErrors.errors);

  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('errors');
  const [activeSubTab, setActiveSubTab] = useState('allFields');
  const userManuallySwitchedRef = useRef(false);
  const hadValidationErrorsRef = useRef(false);
  
  const extractedData = useSelector(state => state.documents.extractedData);
  const stepsCompleted = useSelector(state => state.documents.stepsCompleted);
  
  // Switch to errors tab when validation errors change (after save/generation)
  useEffect(() => {
    const hasErrors = (validationErrors && validationErrors.length > 0) || taricJsonErrors;
    
    // Track if we had validation errors before
    if (hasErrors) {
      hadValidationErrorsRef.current = true;
    }
    
    // If there are errors, always switch to errors tab (even if user manually switched away)
    if (hasErrors) {
      setActiveTab('errors');
      userManuallySwitchedRef.current = false; // Reset manual switch flag when errors occur
    }
    // If no errors, switch to fieldOverview (only if we're currently on errors tab to avoid unnecessary switches)
    else if (!hasErrors) {
      setActiveTab(prevTab => {
        if (prevTab === 'errors') {
          userManuallySwitchedRef.current = false;
          return 'fieldOverview';
        }
        return prevTab;
      });
    }
  }, [validationErrors, taricJsonErrors]);
  
  // Track when user manually switches to fieldOverview
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'fieldOverview') {
      userManuallySwitchedRef.current = true;
    } else if (tab === 'errors') {
      userManuallySwitchedRef.current = false;
    }
  };

  const isUserLoaded = useUserLoaded();
  
  useCompany();
  useSenders();
  useReceivers();
  useAgents();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const storedValue = localStorage.getItem('isSidebarCollapsed');
    return storedValue !== null ? JSON.parse(storedValue) : false;
  });

  const [isFieldOverviewCollapsed, setIsFieldOverviewCollapsed] = useState(() => {
    const storedValue = localStorage.getItem('isFieldOverviewCollapsed');
    return storedValue !== null ? JSON.parse(storedValue) : false;
  });

  const [isGenerateTabOpen, setIsGenerateTabOpen] = useState(false);
  const [isGenerateTabCollapsed, setIsGenerateTabCollapsed] = useState(false);

  // Check if current route is a declaration route
  const isDeclarationRoute = location.pathname.startsWith('/declaration/') || 
                             location.pathname.startsWith('/declaration-management');

  // Close GenerateTab when navigating away from declaration routes
  useEffect(() => {
    if (!isDeclarationRoute && isGenerateTabOpen) {
      setIsGenerateTabOpen(false);
      setIsGenerateTabCollapsed(false);
      localStorage.setItem('isGenerateTabCollapsed', JSON.stringify(false));
    }
  }, [location.pathname, isDeclarationRoute, isGenerateTabOpen]);

  // Global listener for openGenerateTab event - ensures it works even if GenerateTab isn't fully mounted
  useEffect(() => {
    const handleOpenGenerateTab = () => {
      // Only handle if we're on a route where GenerateTab should be shown
      if (isDeclarationRoute) {
        setIsGenerateTabOpen(true);
        setIsGenerateTabCollapsed(false);
        // Collapse sidebar if not already collapsed
        if (!isSidebarCollapsed) {
          setIsSidebarCollapsed(true);
          localStorage.setItem('isSidebarCollapsed', JSON.stringify(true));
        }
      }
    };

    window.addEventListener('openGenerateTab', handleOpenGenerateTab);
    return () => {
      window.removeEventListener('openGenerateTab', handleOpenGenerateTab);
    };
  }, [isDeclarationRoute, isSidebarCollapsed]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(prevState => {
      const newState = !prevState;
      localStorage.setItem('isSidebarCollapsed', JSON.stringify(newState));
      return newState;
    });
  };

  // Listen for sidebar collapse events from ProcessingModal
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      if (event.detail && event.detail.collapsed !== undefined) {
        setIsSidebarCollapsed(event.detail.collapsed);
      }
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => {
      window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
    };
  }, []);

  // Animation
  useEffect(() => {
    const sections = document.querySelectorAll('.fadeUp');
    const tl = gsap.timeline();

    sections.forEach(section => {
      tl.to(section,
        {
          stagger: 0.15,
          duration: 0.15,
          opacity: 1,
          y: 0,
          ease: "back",
        }
      );
    });
  }, [location]);


  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = sessionStorage.getItem('token');
      const expires = sessionStorage.getItem('expires');
      const refreshToken = sessionStorage.getItem('refresh_token');

      if (!token) {
        navigate("/login");
        return;
      } 
      
      if (expires) {
        const expirationTime = parseInt(expires, 10);
        const currentTime = new Date().getTime();
        const timeUntilExpiry = expirationTime - currentTime;

        // Check if the token has expired
        if (currentTime >= expirationTime) {
          // Token is expired - proactively refresh it
          if (!refreshToken) {
            handleLogout();
            return;
          } else {
            // Proactively refresh the token and wait for it to complete
            try {
              const { refreshAccessToken } = await import('./utils/api');
              const data = await refreshAccessToken();
              if (!data) {
                // Check if refresh token still exists (another concurrent refresh might have succeeded)
                const stillHasRefreshToken = sessionStorage.getItem('refresh_token');
                if (stillHasRefreshToken) {
                  // Check if we now have a valid token
                  const newToken = sessionStorage.getItem('token');
                  if (newToken) {
                    return;
                  }
                }
                handleLogout();
                return;
              } else {
                // Token refreshed, continue with app
              }
            } catch (error) {
              // Check if refresh token still exists (another concurrent refresh might have succeeded)
              const stillHasRefreshToken = sessionStorage.getItem('refresh_token');
              if (stillHasRefreshToken) {
                const newToken = sessionStorage.getItem('token');
                if (newToken) {
                  return;
                }
              }
              handleLogout();
              return;
            }
          }
        } else {
          const timeout = expirationTime - currentTime;
          
          const logoutTimer = setTimeout(async () => {
            // On expiration, proactively refresh the token
            const currentRefreshToken = sessionStorage.getItem('refresh_token');
            if (!currentRefreshToken) {
              handleLogout();
            } else {
              try {
                const { refreshAccessToken } = await import('./utils/api');
                const data = await refreshAccessToken();
                if (!data) {
                  // Check if refresh token still exists (another concurrent refresh might have succeeded)
                  const stillHasRefreshToken = sessionStorage.getItem('refresh_token');
                  if (stillHasRefreshToken) {
                    // Check if we now have a valid token
                    const newToken = sessionStorage.getItem('token');
                    if (newToken) {
                      return;
                    }
                  }
                  handleLogout();
                } else {
                }
              } catch (error) {
                // Check if refresh token still exists (another concurrent refresh might have succeeded)
                const stillHasRefreshToken = sessionStorage.getItem('refresh_token');
                if (stillHasRefreshToken) {
                  const newToken = sessionStorage.getItem('token');
                  if (newToken) {
                    return;
                  }
                }
                handleLogout();
              }
            }
          }, timeout);

          return () => {
            clearTimeout(logoutTimer);
          };
        }
      } else {
      }
    };

    checkAndRefreshToken();
  }, []); // Empty dependency array - only run once on mount 

  const [showOptions, setShowOptions] = useState(false);
  const [backtoDashboardClicked, setBacktoDashboardClicked] = useState(false);
  const [scrollToID, setScrollToID] = useState("");
  

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('expires');
    sessionStorage.removeItem('isDarkMode');
    sessionStorage.removeItem('email');
    sessionStorage.removeItem('user_id');
    navigate('/login');
    toast.success(getMainTranslation('successfullyLoggedOut', isSpanish));
  };

  const handleBacktoDashboard = () => {
    navigate("/");
    if (location.pathname === "/user-management") {
      setScrollToID("review-user");
    } else if (location.pathname === "/sender-management") {
      setScrollToID("review-user");
    } else if (location.pathname === "/receiver-management") {
      setScrollToID("review-user");
    } else {
      setScrollToID("review-agents");
    }
    setBacktoDashboardClicked(true);
  };

  const [shouldShowButton, setShouldShowButton] = useState(false);

  useEffect(() => {
    setShouldShowButton(showBacktoDashboardButton && (location.pathname !== "/"));
  }, [showBacktoDashboardButton, location]);

  const [isSpanish, setIsSpanish] = useLanguage();

  const [showHeaderShadow, setShowHeaderShadow] = useState(false);

  useEffect(() => {
    let timeoutId;
    let content;
    let cleanup;
    
    const setupScrollListener = () => {
      content = document.getElementById('analysis-report');
      if (!content) {
        // Retry if element doesn't exist yet
        timeoutId = setTimeout(setupScrollListener, 100);
        return;
      }
      
      const handleScroll = () => {
        const scrollTop = content.scrollTop || 0;
        setShowHeaderShadow(scrollTop >= 40);
      };
      
      // Initial check
      handleScroll();
      
      // Add scroll listener with passive option for better performance
      content.addEventListener('scroll', handleScroll, { passive: true });
      
      cleanup = () => {
        if (content) {
          content.removeEventListener('scroll', handleScroll);
        }
      };
    };

    setupScrollListener();
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (cleanup) {
        cleanup();
      }
    };
  }, [location.pathname]);

  const { current, usage, exportCost, loading, error } = useSelector(state => state.creditStats);

  useEffect(() => {
    dispatch(fetchCreditStats());
  }, [dispatch]);

  // Get isDirty from Redux and use hook for save/cancel functions
  const isDirty = useSelector(state => state.documents.isDirty);
  const lastEditedDocumentField = useSelector(state => state.documents.lastEditedDocumentField);
  const lastEditedDocumentValue = useSelector(state => state.documents.lastEditedDocumentValue);
  const lastEditedDocumentDeclarationIndex = useSelector(state => state.documents.lastEditedDocumentDeclarationIndex);
  const lastEditedDocumentPartidaIndex = useSelector(state => state.documents.lastEditedDocumentPartidaIndex);
  const editableData = useSelector(state => state.documents.editableData);
  
  const {
    saveChanges: originalSaveChanges,
    cancelChanges,
    setEditableData: updateEditableData
  } = useDeclarationSummary(extractedData);
  
  // Wrap saveChanges to reset the validation errors ref after save
  const saveChanges = () => {
    const result = originalSaveChanges();
    if (result?.success === false) {
      toast.error(getMainTranslation('fixAllRequiredBeforeSave', isSpanish));
      return;
    }
    toast.success(getMainTranslation('changesSaved', isSpanish));
    // Reset the ref after save so it doesn't keep pulsing
    hadValidationErrorsRef.current = false;
    // Clear the last edited document field so "Apply to All" button disappears
    dispatch(clearLastEditedDocumentField());
  };

  const handleCancelChanges = () => {
    cancelChanges();
    toast.success(getMainTranslation('changesCancelled', isSpanish));
    dispatch(clearLastEditedDocumentField());
    hadValidationErrorsRef.current = false;
  };
  
  // Handler to apply document field changes to all documents
  const handleApplyToAllDocuments = () => {
    if (
      lastEditedDocumentDeclarationIndex === null ||
      lastEditedDocumentPartidaIndex === null ||
      !editableData
    ) {
      return;
    }
    
    // Deep clone to avoid mutating original data
    const updatedData = JSON.parse(JSON.stringify(editableData));
    const declaration = updatedData[lastEditedDocumentDeclarationIndex];
    if (!declaration || !Array.isArray(declaration.partidas)) return;
    const sourcePartida = declaration.partidas[lastEditedDocumentPartidaIndex];
    if (!sourcePartida) return;
    const templateDocuments = JSON.parse(JSON.stringify(sourcePartida.documentos || []));
    
    declaration.partidas.forEach((partida, partidaIndex) => {
      if (partidaIndex === lastEditedDocumentPartidaIndex) return;
      partida.documentos = JSON.parse(JSON.stringify(templateDocuments));
    });
    
    // Update editableData and extractedData
    updateEditableData(updatedData);
    dispatch(setDeclarationDirty(true));
    // Clear the last edited field after applying
    dispatch(clearLastEditedDocumentField());
  };

  if (sessionStorage.getItem('token') && !isUserLoaded) {
    return (
      <div className="loading-container absolute right-0 left-0 bottom-0 top-0 flex items-center justify-center flex-col gap-2">
        <div className="mr-3 w-5 h-5 flex flex-col">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p>{getMainTranslation('loadingYourData', isSpanish)}</p>
      </div>
    );
  }

  return (
    <div className="App">
      {sessionStorage.getItem('token') && (
        <>
          
          <main>
            <Sidebar
              isDarkMode={isDarkMode}
              setIsDarkMode={setIsDarkMode}
              isSidebarCollapsed={isSidebarCollapsed} 
              setIsSidebarCollapsed={toggleSidebar}
            />
            <div className={`content min-h-screen max-h-screen overflow-y-auto ${isDarkMode ? "dark-mode" : ""} py-10 pt-10 ${isSidebarCollapsed && "content-sidebar-collapsed"} ${taricJsonErrors ? "taric-json-error px-6" : "px-12"} ${isFieldOverviewCollapsed && "pr-0"}}`} id="analysis-report">
              <div className={`${location.pathname === "/" && extractedData ? (isFieldOverviewCollapsed ? "right-[4.75rem]" : "right-80") : "right-8"} ${location.pathname !== "/" && "right-8" } ${isGenerateTabOpen && !isGenerateTabCollapsed ? "right-[22rem]" : ""} top-8 z-[1001] flex justify-end fixed gap-2`}>
                {((isDirty && extractedData) || (lastEditedDocumentField && extractedData)) && (
                  <div className={`flex gap-2 bg-white shadow-md rounded-full p-2 ${(!validationErrors || validationErrors.length === 0) && (!taricJsonErrors || taricJsonErrors.length === 0) && hadValidationErrorsRef.current ? 'save-cancel-alert' : ''}`}>
                    {isDirty && extractedData && (
                      <>
                        <button
                          className="bg-red-50 rounded-full flex items-center py-2 px-6 gap-2"
                          onClick={handleCancelChanges}
                          title="Cancel Changes"
                        >
                          <X className="stroke-red-500 size-4" />
                          <span className="text-red-500 text-xs">{getDeclarationSummaryTranslation('cancel', isSpanish)}</span>
                        </button>
                        <button
                          className="bg-primary rounded-full flex items-center py-2 px-6 gap-2"
                          onClick={saveChanges}
                          title="Save Changes"
                        >
                          <Save className="size-4" />
                          <span className="text-xs">{getDeclarationSummaryTranslation('save', isSpanish)}</span>
                        </button>
                      </>
                    )}
                    {/* Apply to All Documents button - only show when a document field has been edited */}
                    {lastEditedDocumentField && extractedData && (
                      <button
                        className="bg-primary rounded-full flex items-center py-2 px-6 gap-2"
                        onClick={handleApplyToAllDocuments}
                        title={getDeclarationSummaryTranslation('saveAllDocumentsTooltip', isSpanish)}
                      >
                        <Save className="size-4" />
                        <span className="text-xs">{getDeclarationSummaryTranslation('saveAllDocuments', isSpanish)}</span>
                      </button>
                    )}
                  </div>
                )}
                {/* Export to Taric button - only show on /declaration/:id route */}
                {location.pathname.startsWith('/declaration/') && (
                  <div className={`flex gap-2 bg-white rounded-full p-2 ${showHeaderShadow ? 'shadow-xl' : ''}`}>
                    <button
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('openGenerateTab'));
                      }}
                      className="bg-default rounded-full flex items-center gap-2 py-2 px-6"
                      title={getDeclarationManagementTranslation('exportToTaric', isSpanish)}
                    >
                      <FileJson className="size-5 stroke-white" />
                      <span className="text-white text-xs">
                        {getDeclarationManagementTranslation('exportToTaric', isSpanish)}
                      </span>
                    </button>
                  </div>
                )}
                <div className={`bg-white h-14 p-2 rounded-full flex justify-end gap-2 transition-shadow ${showHeaderShadow ? 'shadow-xl' : ''}`} id="review-extracted-data2">
                {/* Only show reset button when stuck scenario occurs: originalExtractedData exists in sessionStorage but stepsCompleted is false */}
                {(() => {
                  const originalExtractedDataStr = sessionStorage.getItem('originalExtractedData');
                  const hasOriginalExtractedData = originalExtractedDataStr !== null;
                  try {
                    if (hasOriginalExtractedData) {
                      const parsed = JSON.parse(originalExtractedDataStr);
                      const isValid = Array.isArray(parsed) 
                        ? parsed.length > 0 && parsed.every(d => d !== null && d !== undefined && typeof d === 'object' && Object.keys(d).length > 0)
                        : parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
                      return isValid && !stepsCompleted;
                    }
                  } catch (e) {
                    return false;
                  }
                  return false;
                })() && (
                  <button
                    onClick={() => {
                      resetAppState(dispatch);
                      toast.success(getMainTranslation('resetAppSuccess', isSpanish) || 'App state reset successfully');
                      // Reload page to ensure clean state
                      window.location.reload();
                    }}
                    className="cursor-pointer h-10 flex items-center gap-2 py-2 px-4 rounded-full transition-colors bg-gray-100 hover:bg-gray-200"
                    title={getMainTranslation('resetApp', isSpanish) || 'Reset App State'}
                  >
                    <RotateCcw className="h-5 w-5" />
                    <span className="text-sm">{getMainTranslation('reset', isSpanish) || 'Reset'}</span>
                  </button>
                )}
                <CreditSnippet
                    current={current}
                    usage={usage}
                    exportCost={exportCost}
                    loading={loading}
                    error={error}
                    isStripeModalOpen={isStripeModalOpen}
                    setIsStripeModalOpen={setIsStripeModalOpen}
                />
                <button
                onClick={() => { setIsSpanish(!isSpanish) }}
                className={`cursor-pointer h-10 flex items-center gap-2 py-2 px-4 rounded-full transition-colors ${isSpanish ? "bg-yellow-100" : "bg-blue-100"}`}
              >
              <img src={isSpanish ? spanish : english} alt="Logo" className="h-5" /> 
                <p className={`text`}>{isSpanish ? getMainTranslation('esp', isSpanish) : getMainTranslation('eng', isSpanish)}</p>
              </button>
                  <div className="circle bg-default mb-10 cursor-pointer" onClick={toggleOptions}>
                    <span className="text-white">{userData?.first_name?.charAt(0).toUpperCase() || 'U'}</span>
                  </div>
                  {showOptions && (
                    <div className="options rounded-2xl bg-white p-6 flex flex-col items-center shadow-md">
                      <div className="!w-20 !h-20 circle bg-default mb-4 cursor-pointer" 
                      onClick={toggleOptions}>
                        <span className="text-white text-3xl">{userData?.first_name?.charAt(0).toUpperCase() || 'U'}</span>
                      </div>
                      <h1 className="font-bold">{userData?.first_name} {userData?.last_name}</h1>
                      <p className="opacity-75 text-sm">{sessionStorage.getItem('email')}</p>
                      <div className="divider mt-8"></div>
                      <span className='options-item flex-1 w-full mt-2' onClick={handleLogout}>
                        <img
                          src={logoutIcon}
                          alt="Logout"
                          className="profile-icon" />
                        <p className='text-sm'>{getMainTranslation('logout', isSpanish)}</p>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <Routes>
                <Route path="/" element={<Dashboard
                  showBacktoDashboardButton={showBacktoDashboardButton}
                  setBacktoDashboardClicked={setBacktoDashboardClicked}
                  backtoDashboardClicked={backtoDashboardClicked}
                  scrollToID={scrollToID}
                  setIsStripeModalOpen={setIsStripeModalOpen}
                />} />
                <Route path="/user-management" element={<UserManagement />} />
                <Route path="/sender-management" element={<SenderManagement />} />
                <Route path="/receiver-management" element={<ReceiverManagement />} />
                <Route path="/customs-agent" element={<CustomsAgent />} />
                <Route path="/declaration-management" element={<DeclarationManagement />} />
                <Route 
                  path="/declaration/:id" 
                  element={<DeclarationView />
                  } 
                />
                <Route path="/success" element={<Success />} />
                <Route path="/credit-history" element={<Payments />} />
                <Route path="/cancel" element={<Cancel />} />
              </Routes>
              {shouldShowButton && (
                <button className="bg-default py-4 px-8 rounded-full dashboard-button flex gap-2" onClick={handleBacktoDashboard}>
                  <ArrowUpLeft className='w-6 h-6 stroke-white' />
                  <p className="text-white" >{getMainTranslation('goBackToHome', isSpanish)}</p>
                </button>
              )}
            </div>
            
            {location.pathname === "/" && extractedData && (
              <FieldOverviewPanel
                extractedData={extractedData}
                activeTab={activeTab}
                activeSubTab={activeSubTab}
                setActiveTab={handleTabChange}
                setActiveSubTab={setActiveSubTab}
                isSpanish={isSpanish}
                validationErrors={validationErrors}
                taricJsonErrors={taricJsonErrors}
                ValidationErrors={ValidationErrors}
                TaricJsonErrors={TaricJsonErrors}
                location={location}
                isFieldOverviewCollapsed={isFieldOverviewCollapsed}
                setIsFieldOverviewCollapsed={(collapsed) => {
                  setIsFieldOverviewCollapsed(collapsed);
                  localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(collapsed));
                }}
              />
            )}

            {/* Generate Tab - Right Side Panel - Only show on declaration routes */}
            {isDeclarationRoute && (
              <GenerateTab 
                isSidebarCollapsed={isSidebarCollapsed}
                setIsSidebarCollapsed={setIsSidebarCollapsed}
                setIsGenerateTabOpen={setIsGenerateTabOpen}
                setIsGenerateTabCollapsed={setIsGenerateTabCollapsed}
                isGenerateTabOpen={isGenerateTabOpen}
              />
            )}
          </main>
          {isStripeModalOpen && (
            <StripePaymentModal isOpen={isStripeModalOpen} onClose={() => setIsStripeModalOpen(false)} />
          )}
        </>
      )}
    </div>
  );
}

export default Main;